import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Input,
  TextField,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import Tabs from 'components/Tabs';
import CreateProcessContext from 'shared/contexts/CreateProcessContext';
import { getCreateProcessStyles } from 'components/modals/CreateProcessModal';
import { DICTIONARY } from 'shared/constants';
import {
  ArchivedState,
  Company,
  Employer,
  ForeignNational,
} from 'API';
import PersonService from 'services/person';
import {
  checkNullObject,
  fetchListAll,
  getCacheWithExpiration,
  isValidEmail,
  notNullFilter,
} from 'shared/utilities';
import { searchForeignNationals } from 'graphql/queries';
import UserContext from 'shared/contexts/UserContext';
import { Autocomplete } from '@mui/material';
import { nanoid } from 'nanoid';
import IconRow from 'components/IconRow';
import IconRowCell from 'components/IconRowCell';
import Panel from './Panel';

const { fullname } = PersonService;

const NextButton: React.FC = () => {
  const theme = useTheme();
  const styles = getCreateProcessStyles(theme);
  const { goToPanel, process } = useContext(CreateProcessContext);

  const canGoToNext = process.foreignNationalID
    || (process.employeeData?.firstname
      && process.employeeData?.lastname
      && (!process.employeeData?.email
        || isValidEmail(process.employeeData?.email || '')));

  return (
    <Button
      sx={styles.buttonMain}
      color="primary"
      disabled={!canGoToNext}
      onClick={() => {
        goToPanel(process.foreignNationalID ? 'select' : 'startDependents');
      }}
      endIcon={<ArrowForwardIcon />}
    >
      Let’s get started
    </Button>
  );
};

const ExistingEmployee: React.FC = () => {
  const theme = useTheme();
  const styles = getCreateProcessStyles(theme);
  const {
    process, setProcess, setSelectedCompany,
  } = useContext(CreateProcessContext);
  const [userState] = useContext(UserContext);
  const { person } = userState;
  const [isLoading, setIsLoading] = useState(true);
  const [existingEmployees, setExistingEmployees] = useState<
  Array<ForeignNational>
  >([]);

  const handleEmployeeChange = (
    event: any, value: ForeignNational | null,
  ) => {
    setProcess((draft) => {
      draft.foreignNationalID = value?.id;
      draft.companyID = value?.companyID;
    });
  };

  useEffect(() => {
    let ignore = false;
    const companyIDs = ((person as Employer).companies?.items || [])
      .filter(notNullFilter)
      .map((item) => item!.company!.id);

    setProcess((draft) => {
      draft.employeeData = {
        firstname: '',
        lastname: '',
        tempID: nanoid(),
      };
    });
    setSelectedCompany(undefined);

    const getExistingEmployees = async () => {
      const nationals = await getCacheWithExpiration(
        'existing-employees',
        async () => fetchListAll(
          searchForeignNationals, {
            filter: {
              and: [
                { archived: { ne: ArchivedState.ARCHIVED } },
                { or: companyIDs.map((id) => ({ companyID: { eq: id } })) },
              ],
            },

          },
        ),
      ) as Array<ForeignNational>;

      if (!ignore) {
        setExistingEmployees(nationals);
        setIsLoading(false);
      }
    };

    getExistingEmployees();

    return () => {
      ignore = true;
    };
  }, []);

  const foreignNationals = checkNullObject(existingEmployees);

  return (
    <Box sx={styles.employeeTab}>
      <Typography style={{ marginBottom: '1rem' }}>
        Search for an existing employee.
      </Typography>
      {isLoading ? (
        <CircularProgress />
      ) : (
        <IconRow sx={styles.formControl} isInput>
          <IconRowCell type="icon"><SearchIcon color="primary" /></IconRowCell>
          <IconRowCell type="text">
            <Autocomplete
              options={foreignNationals}
              getOptionLabel={(option) => fullname(option)}
              isOptionEqualToValue={(option) => process.foreignNationalID === option.id}
              onChange={handleEmployeeChange}
              value={
                foreignNationals?.find(
                  (option) => process.foreignNationalID === option.id,
                ) || null
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label={DICTIONARY.ForeignNational}
                />
              )}
            />
          </IconRowCell>
        </IconRow>
      )}
      <NextButton />
    </Box>
  );
};

const NewEmployee: React.FC = () => {
  const theme = useTheme();
  const styles = getCreateProcessStyles(theme);
  const {
    process, setProcess, setSelectedCompany,
  } = useContext(CreateProcessContext);
  const [userState] = useContext(UserContext);
  const { person } = userState;

  const companies:Array<Company> = ((person as unknown as Employer)
    .companies?.items?.filter(notNullFilter)
    .map((item) => item!.company) || []) as Array<Company>;

  const handleChange = (
    event: React.ChangeEvent<{
      name: string;
      value: string;
    }>,
  ) => {
    const { value } = event.target;
    const name = event.target.name as 'email' | 'firstname' | 'lastname';

    setProcess((draft) => {
      draft.employeeData![name] = value;
    });
  };

  const handleCompanySelect = (e: any) => {
    setProcess((draft) => {
      draft.companyID = e.target.value;
    });
    setSelectedCompany(
      companies.find((company) => company.id === e.target.value || undefined),
    );
  };

  useEffect(() => {
    // Reset the existing national ID, to prevent picking two
    setProcess((draft) => {
      draft.foreignNationalID = null;
    });
  }, []);

  useEffect(() => {
    if (companies.length > 0) {
      setProcess((draft) => {
        draft.companyID = companies[0]!.id;
      });
      setSelectedCompany(companies[0]);
    } else {
      setSelectedCompany(undefined);
    }
  }, [companies.length]);

  return (
    <Box sx={styles.employeeTab}>
      <Typography style={{ marginBottom: '2rem' }}>
        Add new employee’s information.
      </Typography>
      <FormControl sx={styles.formControl}>
        <InputLabel htmlFor="fname-input">
          {`${DICTIONARY.Firstname} *`}
        </InputLabel>
        <Input
          id="fname-input"
          name="firstname"
          value={process.employeeData?.firstname || ''}
          onChange={handleChange}
          required
        />
      </FormControl>

      <FormControl sx={styles.formControl}>
        <InputLabel htmlFor="lname-input">
          {`${DICTIONARY.Lastname} *`}
        </InputLabel>
        <Input
          id="lname-input"
          name="lastname"
          value={process.employeeData?.lastname || ''}
          onChange={handleChange}
        />
      </FormControl>

      <FormControl sx={styles.formControl}>
        <InputLabel htmlFor="email-input">{DICTIONARY.EmailAddress}</InputLabel>
        <Input
          id="email-input"
          name="email"
          value={process.employeeData?.email || ''}
          onChange={handleChange}
        />
      </FormControl>

      {companies.length > 1 && process.companyID && (
        <FormControl sx={styles.formControl}>
          <InputLabel htmlFor="company-input" id="process-company-label">
            {DICTIONARY.Company}
          </InputLabel>
          <Select
            labelId="process-company-label"
            id="process-company-select"
            value={process.companyID}
            onChange={handleCompanySelect}
            style={{ marginTop: '1.5rem' }}
          >
            {companies.map((company) => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <NextButton />
    </Box>
  );
};

const CreateProcessModalPanelStart: React.FC = () => {
  const employeeTabs: Array<Tab> = [
    {
      title: 'Existing Employee',
      component: ExistingEmployee,
      icon: React.createElement(AccountCircleIcon),
    },
    {
      title: 'New Employee',
      component: NewEmployee,
      icon: React.createElement(AccountCircleIcon),
    },
  ];

  return (
    <Panel>
      <Box>
        <Tabs
          tabs={employeeTabs}
          label="New or existing employee"
          spreadTabs
        />
      </Box>
    </Panel>
  );
};

export default CreateProcessModalPanelStart;
